// src/pages/WHSReturnToWork.tsx — Return to Work (RTW) Management
// WHS Sprint Item 10. Triggered from Incidents with MEDICAL or HOSPITAL treatment.
// Tracks RTW plans from initial assessment through graduated return to full duties.
// Complies with Workers' Compensation and Rehabilitation Act 2003 (Qld) Part 3.
// WHSO and PM can create plans, record check-ins, and advance stages.

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_JOBS }   from '../utils/mockData'
import {
  Heart, Plus, CheckCircle, Clock, AlertTriangle, X, ChevronRight,
  User, Calendar, FileText, Phone, ArrowRight, Download, Shield,
} from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────

type RTWStatus    = 'ACTIVE' | 'COMPLETED' | 'WITHDRAWN'
type StageStatus  = 'PENDING' | 'ACTIVE' | 'COMPLETED' | 'SKIPPED'
type TreatmentType = 'FIRST_AID' | 'MEDICAL' | 'HOSPITAL'

interface RTWStage {
  id: string
  number: number
  title: string
  hoursPerDay: number
  restrictions: string[]
  startDate: string
  endDate: string | null
  status: StageStatus
  completedAt: string | null
}

interface RTWCheckIn {
  id: string
  date: string
  checkedByName: string
  workerPresent: boolean
  dutiesPerformed: string
  workerFeedback: string
  medicalClearanceObtained: boolean
  nextStepRecommendation: string
  escalationRequired: boolean
}

interface RTWPlan {
  id: string
  incidentId: string
  incidentNumber: string
  workerName: string
  jobId: string
  jobNumber: string
  injuryDescription: string
  treatmentType: TreatmentType
  createdAt: string
  createdByName: string
  status: RTWStatus
  treatingPractitioner: string
  practitionerPhone: string
  initialCapacity: string
  nextReviewDate: string
  stages: RTWStage[]
  checkIns: RTWCheckIn[]
}

// ─── Config ───────────────────────────────────────────────────────

const STATUS_CFG: Record<RTWStatus, { label: string; bg: string; text: string; dot: string }> = {
  ACTIVE:    { label: 'Active',    bg: 'bg-blue-50',  text: 'text-blue-700',  dot: 'bg-blue-500'  },
  COMPLETED: { label: 'Completed', bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  WITHDRAWN: { label: 'Withdrawn', bg: 'bg-gray-50',  text: 'text-gray-500',  dot: 'bg-gray-400'  },
}

const STAGE_CFG: Record<StageStatus, { label: string; bg: string; text: string; ring: string }> = {
  PENDING:   { label: 'Pending',   bg: 'bg-gray-100',   text: 'text-gray-500',  ring: 'ring-gray-200'   },
  ACTIVE:    { label: 'Active',    bg: 'bg-blue-100',   text: 'text-blue-700',  ring: 'ring-blue-300'   },
  COMPLETED: { label: 'Complete',  bg: 'bg-green-100',  text: 'text-green-700', ring: 'ring-green-300'  },
  SKIPPED:   { label: 'Skipped',   bg: 'bg-gray-100',   text: 'text-gray-400',  ring: 'ring-gray-200'   },
}

const TREATMENT_CFG: Record<TreatmentType, { label: string; bg: string; text: string }> = {
  FIRST_AID: { label: 'First aid', bg: 'bg-blue-100',   text: 'text-blue-700'   },
  MEDICAL:   { label: 'Medical',   bg: 'bg-amber-100',  text: 'text-amber-700'  },
  HOSPITAL:  { label: 'Hospital',  bg: 'bg-red-100',    text: 'text-red-700'    },
}

const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'

// ─── Seed data ────────────────────────────────────────────────────
// Two realistic plans — one hospital, one medical treatment.

const DEFAULT_STAGES: RTWStage[] = [
  { id: 's1', number: 1, title: 'Homecare / non-work',           hoursPerDay: 0, restrictions: ['No work duties'], startDate: '2026-05-20', endDate: '2026-05-27', status: 'COMPLETED', completedAt: '2026-05-27T09:00:00Z' },
  { id: 's2', number: 2, title: 'Modified duties — light admin', hoursPerDay: 4, restrictions: ['Right hand not to be used', 'No lifting >0.5kg', 'Office/admin tasks only'], startDate: '2026-05-28', endDate: '2026-06-04', status: 'COMPLETED', completedAt: '2026-06-04T17:00:00Z' },
  { id: 's3', number: 3, title: 'Graduated return — 6h/day',    hoursPerDay: 6, restrictions: ['No tool use with right hand', 'Supervisory/inspection role only'], startDate: '2026-06-05', endDate: '2026-06-18', status: 'COMPLETED', completedAt: '2026-06-18T17:00:00Z' },
  { id: 's4', number: 4, title: 'Full return to pre-injury duties', hoursPerDay: 8, restrictions: [], startDate: '2026-06-19', endDate: null, status: 'ACTIVE', completedAt: null },
]

const SEED_PLANS: RTWPlan[] = [
  {
    id: 'rtw-001',
    incidentId:     'inc-003',
    incidentNumber: 'INC-2026-003',
    workerName:     'Tom Richards',
    jobId:          'j1',
    jobNumber:      'TP-2026-001',
    injuryDescription: 'Laceration to right hand — 3 stitches. Cable caught on metal edge of draw pit. Fully healed but restricted hand use during rehabilitation.',
    treatmentType:  'HOSPITAL',
    createdAt:      '2026-05-20T17:00:00Z',
    createdByName:  'Jane Smith (PM)',
    status:         'ACTIVE',
    treatingPractitioner: 'Dr A. Nguyen, Sandgate Family Medical',
    practitionerPhone: '07 3869 1800',
    initialCapacity: 'No use of right hand for 7 days. Admin duties only from day 8.',
    nextReviewDate: '2026-08-12',
    stages: DEFAULT_STAGES,
    checkIns: [
      { id: 'ci1', date: '2026-05-28T09:00:00Z', checkedByName: 'Jane Smith', workerPresent: true, dutiesPerformed: 'Paperwork review, HSE register entry', workerFeedback: 'Comfortable with light duties. No pain when resting hand.', medicalClearanceObtained: true, nextStepRecommendation: 'Continue Stage 2 as planned. Review 4 Jun.', escalationRequired: false },
      { id: 'ci2', date: '2026-06-04T09:00:00Z', checkedByName: 'Alex Morgan (WHSO)', workerPresent: true, dutiesPerformed: 'Completed admin tasks, attended toolbox talks', workerFeedback: 'Hand healing well. Slight stiffness in morning.', medicalClearanceObtained: true, nextStepRecommendation: 'Ready for Stage 3 — inspection role only. No tool use right hand.', escalationRequired: false },
      { id: 'ci3', date: '2026-06-18T09:00:00Z', checkedByName: 'Alex Morgan (WHSO)', workerPresent: true, dutiesPerformed: 'Supervision and inspection across j1. Completed ITP witnessing.', workerFeedback: 'Feeling ready for full return. No ongoing pain.', medicalClearanceObtained: true, nextStepRecommendation: 'Clear for full duties from 19 Jun subject to GP clearance on 18 Jun.', escalationRequired: false },
    ],
  },
  {
    id: 'rtw-002',
    incidentId:     'inc-004',
    incidentNumber: 'INC-2026-009',
    workerName:     'Danny Tran',
    jobId:          'j4',
    jobNumber:      'TP-2026-004',
    injuryDescription: 'Soft tissue strain — lower back. Awkward lift of cable drum (approx 28kg). GP prescribed 5 days rest, then graduated return with lifting restriction of 5kg.',
    treatmentType:  'MEDICAL',
    createdAt:      '2026-07-29T11:00:00Z',
    createdByName:  'Alex Morgan (WHSO)',
    status:         'ACTIVE',
    treatingPractitioner: 'Dr P. Sharma, Annerley Medical Clinic',
    practitionerPhone: '07 3892 4400',
    initialCapacity: 'No manual handling for 5 days. Graduated return with 5kg lift limit.',
    nextReviewDate: '2026-08-13',
    stages: [
      { id: 's5', number: 1, title: 'Homecare / non-work',           hoursPerDay: 0, restrictions: ['No work duties', 'Rest and physiotherapy'], startDate: '2026-07-29', endDate: '2026-08-02', status: 'COMPLETED', completedAt: '2026-08-02T17:00:00Z' },
      { id: 's6', number: 2, title: 'Modified duties — office/admin', hoursPerDay: 4, restrictions: ['No lifting >5kg', 'Sit/stand alternating', 'No bending at waist'], startDate: '2026-08-03', endDate: null, status: 'ACTIVE', completedAt: null },
      { id: 's7', number: 3, title: 'Graduated return — 6h/day',     hoursPerDay: 6, restrictions: ['No lifting >10kg', 'Physiotherapy-approved tasks only'], startDate: '', endDate: null, status: 'PENDING', completedAt: null },
      { id: 's8', number: 4, title: 'Full return to pre-injury duties', hoursPerDay: 8, restrictions: [], startDate: '', endDate: null, status: 'PENDING', completedAt: null },
    ],
    checkIns: [
      { id: 'ci4', date: '2026-08-03T08:30:00Z', checkedByName: 'Alex Morgan (WHSO)', workerPresent: true, dutiesPerformed: 'HSE file review, timesheet entry, attend pre-start (seated)', workerFeedback: 'Back sore at end of day. Comfortable with current restrictions.', medicalClearanceObtained: true, nextStepRecommendation: 'Continue Stage 2 for one week. Review 10 Aug before Stage 3.', escalationRequired: false },
    ],
  },
]

// ─── Check-in modal ───────────────────────────────────────────────

function CheckInModal({
  planId,
  onClose,
  onSubmit,
  userName,
}: {
  planId: string
  onClose: () => void
  onSubmit: (planId: string, checkIn: RTWCheckIn) => void
  userName: string
}) {
  const [form, setForm] = useState({
    workerPresent:            true,
    dutiesPerformed:          '',
    workerFeedback:           '',
    medicalClearanceObtained: false,
    nextStepRecommendation:   '',
    escalationRequired:       false,
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-semibold text-gray-900">Record RTW check-in</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={form.workerPresent}
                onChange={e => setForm(f => ({ ...f, workerPresent: e.target.checked }))}
                className="accent-blue-600" />
              Worker was present and engaged with check-in
            </label>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Duties performed today *</label>
            <textarea value={form.dutiesPerformed}
              onChange={e => setForm(f => ({ ...f, dutiesPerformed: e.target.value }))}
              className="input w-full resize-none h-16 text-sm"
              placeholder="e.g. Attended pre-start (seated), completed timesheet entries, reviewed drawings" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Worker feedback *</label>
            <textarea value={form.workerFeedback}
              onChange={e => setForm(f => ({ ...f, workerFeedback: e.target.value }))}
              className="input w-full resize-none h-14 text-sm"
              placeholder="Worker's reported pain level, comfort with duties, any concerns raised" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Next step recommendation</label>
            <textarea value={form.nextStepRecommendation}
              onChange={e => setForm(f => ({ ...f, nextStepRecommendation: e.target.value }))}
              className="input w-full resize-none h-14 text-sm"
              placeholder="Continue current stage / advance to Stage X / escalate to GP" />
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={form.medicalClearanceObtained}
                onChange={e => setForm(f => ({ ...f, medicalClearanceObtained: e.target.checked }))}
                className="accent-green-600" />
              Medical clearance obtained for current stage
            </label>
            <label className="flex items-center gap-2 text-sm text-red-700 cursor-pointer">
              <input type="checkbox" checked={form.escalationRequired}
                onChange={e => setForm(f => ({ ...f, escalationRequired: e.target.checked }))}
                className="accent-red-600" />
              Escalation required (GP / management)
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button
              onClick={() => {
                const ci: RTWCheckIn = {
                  id: `ci_${Date.now()}`,
                  date: new Date().toISOString(),
                  checkedByName: userName,
                  ...form,
                }
                onSubmit(planId, ci)
                onClose()
              }}
              disabled={!form.dutiesPerformed || !form.workerFeedback}
              className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-40">
              Save check-in
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── RTW Plan detail ──────────────────────────────────────────────

function RTWPlanDetail({
  plan,
  onClose,
  onAddCheckIn,
  onAdvanceStage,
  onCompletePlan,
  canEdit,
}: {
  plan: RTWPlan
  onClose: () => void
  onAddCheckIn: (planId: string, ci: RTWCheckIn) => void
  onAdvanceStage: (planId: string) => void
  onCompletePlan: (planId: string) => void
  canEdit: boolean
}) {
  const [showCheckIn, setShowCheckIn] = useState(false)
  const { user } = useAuthStore()
  const activeStage = plan.stages.find(s => s.status === 'ACTIVE')
  const allStagesDone = plan.stages.every(s => s.status === 'COMPLETED' || s.status === 'SKIPPED')
  const lastCheckIn = plan.checkIns[plan.checkIns.length - 1]

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-5 py-4 border-b bg-gray-50">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-xs font-mono text-gray-400">{plan.incidentNumber}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TREATMENT_CFG[plan.treatmentType].bg} ${TREATMENT_CFG[plan.treatmentType].text}`}>
                {TREATMENT_CFG[plan.treatmentType].label}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_CFG[plan.status].bg} ${STATUS_CFG[plan.status].text}`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${STATUS_CFG[plan.status].dot}`} />
                {STATUS_CFG[plan.status].label}
              </span>
            </div>
            <div className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400" />
              {plan.workerName} — RTW Plan
            </div>
            <div className="text-xs text-gray-500 mt-0.5">{plan.injuryDescription}</div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        {/* Medical info */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2 text-sm">
          <div className="text-xs font-semibold text-blue-900 uppercase tracking-wide mb-2">Medical details</div>
          <div className="grid grid-cols-2 gap-2">
            <div><span className="text-xs text-blue-600">Treating practitioner</span><div className="font-medium text-blue-900">{plan.treatingPractitioner || '—'}</div></div>
            <div><span className="text-xs text-blue-600">Phone</span><div className="font-medium text-blue-900 flex items-center gap-1"><Phone className="w-3 h-3" />{plan.practitionerPhone || '—'}</div></div>
            <div className="col-span-2"><span className="text-xs text-blue-600">Initial capacity</span><div className="font-medium text-blue-900">{plan.initialCapacity}</div></div>
            <div><span className="text-xs text-blue-600">Next medical review</span><div className="font-medium text-blue-900 flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(plan.nextReviewDate)}</div></div>
            <div><span className="text-xs text-blue-600">Plan created</span><div className="font-medium text-blue-900">{fmtDate(plan.createdAt)}</div></div>
          </div>
        </div>

        {/* Stage timeline */}
        <div>
          <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">RTW stages</div>
          <div className="relative">
            {/* Connecting line */}
            <div className="absolute left-4 top-5 bottom-5 w-px bg-gray-200 z-0" />
            <div className="space-y-3">
              {plan.stages.map((stage, idx) => {
                const cfg = STAGE_CFG[stage.status]
                return (
                  <div key={stage.id} className="relative flex items-start gap-3">
                    {/* Stage number circle */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 z-10 ring-2 ${cfg.ring} ${cfg.bg} ${cfg.text}`}>
                      {stage.status === 'COMPLETED' ? '✓' : idx + 1}
                    </div>
                    <div className={`flex-1 border rounded-xl p-3 ${stage.status === 'ACTIVE' ? 'border-blue-300 bg-blue-50/50' : stage.status === 'COMPLETED' ? 'border-green-200 bg-green-50/30' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="text-sm font-medium text-gray-900">{stage.title}</div>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>
                      </div>
                      <div className="text-xs text-gray-500 flex flex-wrap gap-2">
                        {stage.hoursPerDay > 0 && <span>{stage.hoursPerDay}h/day</span>}
                        {stage.startDate && <span>From {fmtDate(stage.startDate)}</span>}
                        {stage.completedAt && <span>Completed {fmtDate(stage.completedAt)}</span>}
                      </div>
                      {stage.restrictions.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {stage.restrictions.map((r, i) => (
                            <span key={i} className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded">{r}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
          {canEdit && activeStage && !allStagesDone && plan.status === 'ACTIVE' && (
            <button onClick={() => onAdvanceStage(plan.id)}
              className="mt-3 w-full py-2 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg flex items-center justify-center gap-1.5">
              <ArrowRight className="w-3.5 h-3.5" />Advance to next stage
            </button>
          )}
          {canEdit && allStagesDone && plan.status === 'ACTIVE' && (
            <button onClick={() => onCompletePlan(plan.id)}
              className="mt-3 w-full py-2 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg flex items-center justify-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5" />Mark RTW plan complete — full return to duties
            </button>
          )}
        </div>

        {/* Check-in log */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Check-in log ({plan.checkIns.length})</div>
            {canEdit && plan.status === 'ACTIVE' && (
              <button onClick={() => setShowCheckIn(true)}
                className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1.5 rounded-lg font-medium flex items-center gap-1">
                <Plus className="w-3 h-3" />Add check-in
              </button>
            )}
          </div>
          {plan.checkIns.length === 0 ? (
            <div className="border border-dashed border-gray-200 rounded-xl py-8 text-center text-xs text-gray-400">
              No check-ins recorded yet
            </div>
          ) : (
            <div className="space-y-2">
              {[...plan.checkIns].reverse().map(ci => (
                <div key={ci.id} className={`border rounded-xl p-3 ${ci.escalationRequired ? 'border-red-200 bg-red-50/30' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-700">{fmtDate(ci.date)}</span>
                      <span className="text-xs text-gray-400">by {ci.checkedByName}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {ci.medicalClearanceObtained && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">Med. clearance ✓</span>}
                      {ci.escalationRequired && <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-semibold">⚠ Escalation</span>}
                    </div>
                  </div>
                  <div className="text-xs text-gray-700 mb-0.5"><strong>Duties:</strong> {ci.dutiesPerformed}</div>
                  <div className="text-xs text-gray-600"><strong>Feedback:</strong> {ci.workerFeedback}</div>
                  {ci.nextStepRecommendation && (
                    <div className="text-xs text-blue-700 mt-1 flex items-start gap-1"><ArrowRight className="w-3 h-3 flex-shrink-0 mt-0.5" />{ci.nextStepRecommendation}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Legal note */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-xs text-gray-500">
          <strong className="text-gray-700">WCR Act 2003 (Qld):</strong> Employer must make reasonable duties available and consult with the treating practitioner and worker. RTW plan must be documented and reviewed at each stage. Next review: {fmtDate(plan.nextReviewDate)}.
        </div>
      </div>

      {showCheckIn && (
        <CheckInModal
          planId={plan.id}
          onClose={() => setShowCheckIn(false)}
          onSubmit={(pid, ci) => { onAddCheckIn(pid, ci); setShowCheckIn(false) }}
          userName={user?.fullName ?? 'WHSO'}
        />
      )}
    </div>
  )
}

// ─── New RTW Plan modal ───────────────────────────────────────────

function NewRTWModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void
  onSubmit: (plan: RTWPlan) => void
}) {
  const { user } = useAuthStore()
  const [form, setForm] = useState({
    incidentNumber: '',
    workerName: '',
    jobId: MOCK_JOBS[0]?.id ?? '',
    injuryDescription: '',
    treatmentType: 'MEDICAL' as TreatmentType,
    treatingPractitioner: '',
    practitionerPhone: '',
    initialCapacity: '',
    nextReviewDate: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
  })

  const DEFAULT_NEW_STAGES: RTWStage[] = [
    { id: `ns1_${Date.now()}`, number: 1, title: 'Homecare / non-work',         hoursPerDay: 0, restrictions: ['No work duties'], startDate: new Date().toISOString().slice(0, 10), endDate: null, status: 'ACTIVE',  completedAt: null },
    { id: `ns2_${Date.now()}`, number: 2, title: 'Modified duties — admin/light', hoursPerDay: 4, restrictions: ['No manual handling', 'Seated work only'], startDate: '', endDate: null, status: 'PENDING', completedAt: null },
    { id: `ns3_${Date.now()}`, number: 3, title: 'Graduated return — 6h/day',    hoursPerDay: 6, restrictions: ['Limited manual handling'], startDate: '', endDate: null, status: 'PENDING', completedAt: null },
    { id: `ns4_${Date.now()}`, number: 4, title: 'Full return to pre-injury duties', hoursPerDay: 8, restrictions: [], startDate: '', endDate: null, status: 'PENDING', completedAt: null },
  ]

  const job = MOCK_JOBS.find(j => j.id === form.jobId)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-blue-50">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Heart className="w-4 h-4 text-red-500" />New Return to Work plan
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">4-stage plan created with standard structure. Adjust dates and restrictions after creation.</p>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Incident number</label>
              <input value={form.incidentNumber} onChange={e => setForm(f => ({ ...f, incidentNumber: e.target.value }))}
                className="input text-sm" placeholder="INC-2026-XXX" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Treatment type</label>
              <select value={form.treatmentType} onChange={e => setForm(f => ({ ...f, treatmentType: e.target.value as TreatmentType }))} className="input text-sm">
                <option value="FIRST_AID">First aid</option>
                <option value="MEDICAL">Medical treatment</option>
                <option value="HOSPITAL">Hospital treatment</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Worker name *</label>
              <input value={form.workerName} onChange={e => setForm(f => ({ ...f, workerName: e.target.value }))}
                className="input text-sm" placeholder="Full name" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Job</label>
              <select value={form.jobId} onChange={e => setForm(f => ({ ...f, jobId: e.target.value }))} className="input text-sm">
                {MOCK_JOBS.filter(j => !['COMPLETED', 'CLOSED'].includes(j.status)).map(j => (
                  <option key={j.id} value={j.id}>{j.jobNumber}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Injury description *</label>
              <textarea value={form.injuryDescription} onChange={e => setForm(f => ({ ...f, injuryDescription: e.target.value }))}
                className="input w-full resize-none h-16 text-sm" placeholder="Brief description of injury and affected body part" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Treating practitioner</label>
              <input value={form.treatingPractitioner} onChange={e => setForm(f => ({ ...f, treatingPractitioner: e.target.value }))}
                className="input text-sm" placeholder="Dr Name, Clinic" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Practitioner phone</label>
              <input value={form.practitionerPhone} onChange={e => setForm(f => ({ ...f, practitionerPhone: e.target.value }))}
                className="input text-sm" placeholder="07 XXXX XXXX" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Initial capacity (per GP / specialist)</label>
              <input value={form.initialCapacity} onChange={e => setForm(f => ({ ...f, initialCapacity: e.target.value }))}
                className="input text-sm" placeholder="e.g. No manual handling for 5 days, then 4h/day light duties" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Next medical review date</label>
              <input type="date" value={form.nextReviewDate} onChange={e => setForm(f => ({ ...f, nextReviewDate: e.target.value }))}
                className="input text-sm" />
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800">
            A standard 4-stage RTW plan will be created: Homecare → Modified duties → Graduated return → Full return. Stage dates and restrictions can be adjusted after creation.
          </div>

          <div className="flex gap-2">
            <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button
              onClick={() => {
                const plan: RTWPlan = {
                  id:               `rtw_${Date.now()}`,
                  incidentId:       '',
                  incidentNumber:   form.incidentNumber,
                  workerName:       form.workerName,
                  jobId:            form.jobId,
                  jobNumber:        job?.jobNumber ?? '',
                  injuryDescription: form.injuryDescription,
                  treatmentType:    form.treatmentType,
                  createdAt:        new Date().toISOString(),
                  createdByName:    user?.fullName ?? 'WHSO',
                  status:           'ACTIVE',
                  treatingPractitioner: form.treatingPractitioner,
                  practitionerPhone:    form.practitionerPhone,
                  initialCapacity:      form.initialCapacity,
                  nextReviewDate:       form.nextReviewDate,
                  stages:   DEFAULT_NEW_STAGES,
                  checkIns: [],
                }
                onSubmit(plan)
                onClose()
              }}
              disabled={!form.workerName || !form.injuryDescription}
              className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-40">
              Create RTW plan
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────

export function WHSReturnToWork() {
  const { isTier, user } = useAuthStore()
  const isWHSO = isTier('C_WHSO')
  const isPM   = isTier('B_PM') || isTier('A_DEVELOPER')
  const canEdit = isWHSO || isPM

  const [plans,      setPlans]      = useState<RTWPlan[]>(SEED_PLANS)
  const [selected,   setSelected]   = useState<RTWPlan | null>(null)
  const [showNew,    setShowNew]     = useState(false)
  const [statusFilter, setStatusFilter] = useState<RTWStatus | 'ALL'>('ALL')

  const filtered = plans.filter(p => statusFilter === 'ALL' || p.status === statusFilter)

  const stats = {
    active:    plans.filter(p => p.status === 'ACTIVE').length,
    completed: plans.filter(p => p.status === 'COMPLETED').length,
    overdue:   plans.filter(p => p.status === 'ACTIVE' && p.nextReviewDate && new Date(p.nextReviewDate) < new Date()).length,
  }

  const handleAddCheckIn = (planId: string, ci: RTWCheckIn) => {
    setPlans(ps => ps.map(p => p.id === planId ? { ...p, checkIns: [...p.checkIns, ci] } : p))
    if (selected?.id === planId) {
      setSelected(p => p ? { ...p, checkIns: [...p.checkIns, ci] } : p)
    }
  }

  const handleAdvanceStage = (planId: string) => {
    setPlans(ps => ps.map(p => {
      if (p.id !== planId) return p
      const stages = [...p.stages]
      const activeIdx = stages.findIndex(s => s.status === 'ACTIVE')
      if (activeIdx === -1) return p
      stages[activeIdx] = { ...stages[activeIdx], status: 'COMPLETED', completedAt: new Date().toISOString() }
      if (activeIdx + 1 < stages.length) {
        stages[activeIdx + 1] = {
          ...stages[activeIdx + 1],
          status: 'ACTIVE',
          startDate: new Date().toISOString().slice(0, 10),
        }
      }
      return { ...p, stages }
    }))
    setSelected(p => {
      if (!p || p.id !== planId) return p
      const stages = [...p.stages]
      const activeIdx = stages.findIndex(s => s.status === 'ACTIVE')
      if (activeIdx === -1) return p
      stages[activeIdx] = { ...stages[activeIdx], status: 'COMPLETED', completedAt: new Date().toISOString() }
      if (activeIdx + 1 < stages.length) {
        stages[activeIdx + 1] = { ...stages[activeIdx + 1], status: 'ACTIVE', startDate: new Date().toISOString().slice(0, 10) }
      }
      return { ...p, stages }
    })
  }

  const handleCompletePlan = (planId: string) => {
    setPlans(ps => ps.map(p => p.id === planId ? { ...p, status: 'COMPLETED' } : p))
    setSelected(p => p && p.id === planId ? { ...p, status: 'COMPLETED' } : p)
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Heart className="w-6 h-6 text-red-500" />
            Return to Work
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            RTW plans · graduated return stages · check-in log · medical clearances · WCR Act 2003 (Qld)
          </p>
        </div>
        {canEdit && (
          <button onClick={() => setShowNew(true)}
            className="btn flex items-center gap-2 text-sm bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium">
            <Plus className="w-4 h-4" />New RTW plan
          </button>
        )}
      </div>

      {/* Overdue alert */}
      {stats.overdue > 0 && (
        <div className="bg-red-50 border border-red-300 rounded-xl px-4 py-3 flex items-center gap-3">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <span className="text-sm text-red-800">
            <strong>{stats.overdue} RTW plan{stats.overdue !== 1 ? 's' : ''}</strong> {stats.overdue !== 1 ? 'have' : 'has'} a medical review date that has passed. Contact the treating practitioner and update the plan.
          </span>
        </div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Active plans',   value: stats.active,    bg: stats.active > 0 ? 'bg-blue-50'  : 'bg-gray-50', text: stats.active > 0 ? 'text-blue-700'  : 'text-gray-400', border: stats.active > 0 ? 'border-blue-200' : 'border-gray-200' },
          { label: 'Medical reviews overdue', value: stats.overdue,   bg: stats.overdue > 0 ? 'bg-red-50' : 'bg-gray-50',  text: stats.overdue > 0 ? 'text-red-700'   : 'text-gray-400', border: stats.overdue > 0 ? 'border-red-300'   : 'border-gray-200' },
          { label: 'Completed plans', value: stats.completed, bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
        ].map(s => (
          <div key={s.label} className={`border rounded-xl px-4 py-3 ${s.bg} ${s.border}`}>
            <div className={`text-2xl font-bold ${s.text}`}>{s.value}</div>
            <div className="text-xs text-gray-600 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        {(['ALL', 'ACTIVE', 'COMPLETED', 'WITHDRAWN'] as (RTWStatus | 'ALL')[]).map(f => (
          <button key={f} onClick={() => setStatusFilter(f)}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
              statusFilter === f ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {f === 'ALL' ? 'All plans' : STATUS_CFG[f].label} ({f === 'ALL' ? plans.length : plans.filter(p => p.status === f).length})
          </button>
        ))}
      </div>

      {/* Plan list + detail */}
      <div className="flex gap-4 items-start">
        <div className="flex-1 min-w-0 space-y-2">
          {filtered.length === 0 ? (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl py-16 text-center">
              <Heart className="w-10 h-10 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400 text-sm font-medium">No RTW plans match the current filter</p>
              {canEdit && (
                <button onClick={() => setShowNew(true)} className="mt-3 text-xs text-blue-600 hover:underline">
                  Create a new RTW plan
                </button>
              )}
            </div>
          ) : filtered.map(plan => {
            const isSelected = selected?.id === plan.id
            const activeStage = plan.stages.find(s => s.status === 'ACTIVE')
            const isOverdue = plan.status === 'ACTIVE' && plan.nextReviewDate && new Date(plan.nextReviewDate) < new Date()
            const lastCI = plan.checkIns[plan.checkIns.length - 1]

            return (
              <div key={plan.id}
                onClick={() => setSelected(isSelected ? null : plan)}
                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${
                  isSelected ? 'border-blue-400 ring-2 ring-blue-100 shadow-sm' :
                  isOverdue  ? 'border-red-300 bg-red-50/20' :
                  plan.status === 'COMPLETED' ? 'border-green-200 bg-green-50/10' :
                  'border-gray-200 hover:border-gray-300'
                }`}>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-gray-900 text-sm">{plan.workerName}</span>
                      <span className="text-xs text-gray-400">·</span>
                      <span className="text-xs text-gray-500">{plan.jobNumber}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_CFG[plan.status].bg} ${STATUS_CFG[plan.status].text}`}>
                        {STATUS_CFG[plan.status].label}
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${TREATMENT_CFG[plan.treatmentType].bg} ${TREATMENT_CFG[plan.treatmentType].text}`}>
                        {TREATMENT_CFG[plan.treatmentType].label}
                      </span>
                      {isOverdue && <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium">⚠ Review overdue</span>}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{plan.injuryDescription}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400 flex-wrap">
                      {activeStage && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Stage {activeStage.number}: {activeStage.title}</span>}
                      {lastCI && <span>Last check-in: {fmtDate(lastCI.date)}</span>}
                      <span>Next review: {fmtDate(plan.nextReviewDate)}</span>
                      <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{plan.checkIns.length} check-in{plan.checkIns.length !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 mt-1 ${isSelected ? 'text-blue-500' : 'text-gray-400'}`} />
                </div>
              </div>
            )
          })}
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="w-[440px] flex-shrink-0 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm sticky top-6"
            style={{ maxHeight: 'calc(100vh - 160px)' }}>
            <RTWPlanDetail
              plan={selected}
              onClose={() => setSelected(null)}
              onAddCheckIn={handleAddCheckIn}
              onAdvanceStage={handleAdvanceStage}
              onCompletePlan={handleCompletePlan}
              canEdit={canEdit}
            />
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800 flex items-start gap-2">
        <Shield className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-blue-600" />
        <div>
          <strong>Trigger from Incidents:</strong> RTW plans are created when an incident has treatment type MEDICAL or HOSPITAL. In production, a "Create RTW plan" button will appear automatically in the Incidents module when viewing an incident of this type. Plans link to the originating incident record and can be viewed from both modules.
        </div>
      </div>

      {showNew && <NewRTWModal onClose={() => setShowNew(false)} onSubmit={p => { setPlans(ps => [p, ...ps]); setShowNew(false) }} />}
    </div>
  )
}
